/**
 * UI grouping cho Orders aggregate (EG-78).
 *
 * Tôn trọng @erpgold/shared canonical (11 OrderChannel + OrderState enum) —
 * FE thêm group mapping ở UI layer. BE EG-376 sẽ resolve group → array enum.
 */

import { OrderChannel, OrderState } from '@erpgold/shared';

// ============================================================
// Channel grouping — 11 enum BE → 5 group UI
// ============================================================

export type OrderChannelGroup = 'pos' | 'online' | 'livestream' | 'dealer' | 'phone' | 'other';

export const CHANNEL_GROUP_MAP: Record<OrderChannelGroup, OrderChannel[]> = {
  pos: [OrderChannel.POS],
  online: [OrderChannel.WEB, OrderChannel.APP, OrderChannel.SHOPEE],
  livestream: [OrderChannel.FACEBOOK, OrderChannel.TIKTOK],
  dealer: [OrderChannel.DEALER_PORTAL],
  phone: [OrderChannel.ZALO],
  other: [OrderChannel.OTHER, OrderChannel.BIDV, OrderChannel.BO],
};

export function resolveChannelGroup(channel: OrderChannel): OrderChannelGroup {
  for (const group of Object.keys(CHANNEL_GROUP_MAP) as OrderChannelGroup[]) {
    if (CHANNEL_GROUP_MAP[group].includes(channel)) return group;
  }
  return 'other';
}

export const CHANNEL_GROUP_LABEL: Record<OrderChannelGroup, string> = {
  pos: 'POS',
  online: 'Online',
  livestream: 'Livestream',
  dealer: 'Đại lý',
  phone: 'Điện thoại',
  other: 'Khác',
};

export const CHANNEL_GROUP_ICON: Record<OrderChannelGroup, string> = {
  pos: '🛒',
  online: '🌐',
  livestream: '📺',
  dealer: '💼',
  phone: '📞',
  other: '❓',
};

export const CHANNEL_GROUP_COLOR: Record<OrderChannelGroup, string> = {
  pos: 'cyan',
  online: 'geekblue',
  livestream: 'magenta',
  dealer: 'gold',
  phone: 'purple',
  other: 'default',
};

export const CHANNEL_GROUP_OPTIONS = (
  Object.keys(CHANNEL_GROUP_LABEL) as OrderChannelGroup[]
).map((k) => ({
  label: `${CHANNEL_GROUP_ICON[k]} ${CHANNEL_GROUP_LABEL[k]}`,
  value: k,
}));

// ============================================================
// Status grouping — 10 active OrderState → 4 group UI
// ============================================================

export type OrderStatusGroup = 'pending' | 'processing' | 'completed' | 'cancelled';

export const STATUS_GROUP_MAP: Record<OrderStatusGroup, OrderState[]> = {
  pending: [OrderState.DRAFT, OrderState.PENDING, OrderState.WAIT_PAYMENT],
  processing: [OrderState.PAID, OrderState.PARTIAL_PAID, OrderState.CONFIRMED, OrderState.DELIVERING],
  completed: [OrderState.COMPLETED],
  cancelled: [OrderState.CANCELED, OrderState.RETURNED],
};

export function resolveStatusGroup(state: OrderState): OrderStatusGroup {
  for (const group of Object.keys(STATUS_GROUP_MAP) as OrderStatusGroup[]) {
    if (STATUS_GROUP_MAP[group].includes(state)) return group;
  }
  return 'pending';
}

export const STATUS_GROUP_LABEL: Record<OrderStatusGroup, string> = {
  pending: 'Chờ xử lý',
  processing: 'Đang xử lý',
  completed: 'Hoàn thành',
  cancelled: 'Đã huỷ / Hoàn',
};

export const STATUS_GROUP_COLOR: Record<OrderStatusGroup, string> = {
  pending: 'default',
  processing: 'blue',
  completed: 'green',
  cancelled: 'red',
};

export const STATUS_GROUP_OPTIONS = (
  Object.keys(STATUS_GROUP_LABEL) as OrderStatusGroup[]
).map((k) => ({ label: STATUS_GROUP_LABEL[k], value: k }));

// ============================================================
// Payment label
// ============================================================

export const ORDER_PAYMENT_LABEL: Record<string, string> = {
  cash: 'Tiền mặt',
  transfer: 'Chuyển khoản',
  card: 'Thẻ',
  mix: 'Hỗn hợp',
};
